import {
  ActiveTrackingSessionSnapshot,
  TrackingAggregate,
  TrackingCountMap,
  TrackingPendingQueue,
  TrackingPlatform,
  TrackingScreen,
  TrackingSessionSummary,
  TrackingTechnicalPendingQueue,
  TrackingTechnicalSnapshot,
} from '../types/Tracking/types';

const TRACKING_AGGREGATE_PREFIX = 'TRACKING_AGGREGATE';
const TRACKING_LAST_SESSION_PREFIX = 'TRACKING_LAST_SESSION';
const TRACKING_ACTIVE_SESSION_PREFIX = 'TRACKING_ACTIVE_SESSION';
const TRACKING_TECHNICAL_SNAPSHOT_PREFIX = 'TRACKING_TECHNICAL_SNAPSHOT';
const TRACKING_TECHNICAL_PENDING_QUEUE_PREFIX = 'TRACKING_TECHNICAL_PENDING_QUEUE';
const TRACKING_PENDING_QUEUE_PREFIX = 'TRACKING_PENDING_QUEUE';
const TRACKING_SYNC_DISABLED_ENV_FLAG = 'false';
const TRACKING_DIAGNOSTICS_SYNC_DISABLED_ENV_FLAG = 'false';
const TRACKING_DEBUG_STORAGE_KEY = 'FP_TRACKING_DEBUG';
const TRACKING_SYNC_OVERRIDE_KEY = 'FP_TRACKING_SYNC';
const TRACKING_DIAGNOSTICS_SYNC_OVERRIDE_KEY = 'FP_TRACKING_DIAGNOSTICS_SYNC';
const TRACKING_LOGOUT_FLUSH_TIMEOUT_MS = 2000;
const TRACKING_TECHNICAL_PENDING_QUEUE_MAX_ITEMS = 20;
const TRACKING_VERBOSE_LOGS_KEY = 'FP_TRACKING_VERBOSE_LOGS';

type TrackingLogoutFlushHandler = () => Promise<void>;

let trackingLogoutFlushHandler: TrackingLogoutFlushHandler | null = null;

export const createTrackingId = (prefix = 'tracking'): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const hashUserIdentifier = (value: string): string => {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return (hash >>> 0).toString(16);
};

export const buildTrackingUserKey = (identifier: string): string => {
  const normalizedIdentifier = identifier.trim().toLowerCase();
  if (!normalizedIdentifier) return '';

  return `tracking_user_${hashUserIdentifier(normalizedIdentifier)}`;
};

export const normalizeTrackingPlatform = (platform: string): TrackingPlatform => {
  if (platform === 'ios' || platform === 'android' || platform === 'web') {
    return platform;
  }

  return 'unknown';
};

const normalizeTrackingPathname = (pathname: string): string => {
  if (!pathname) return '/';

  const trimmedPathname = pathname.trim();
  if (!trimmedPathname || trimmedPathname === '/') return '/';

  return trimmedPathname.replace(/\/+$/, '') || '/';
};

const buildFallbackTrackingScreen = (pathname: string): TrackingScreen => {
  const normalizedPathname = normalizeTrackingPathname(pathname);
  if (normalizedPathname === '/') return 'root';

  const normalizedSegments = normalizedPathname
    .replace(/^\//, '')
    .split('/')
    .filter(Boolean)
    .map((segment) => segment.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase())
    .filter(Boolean)
    .join('_');

  return `route:${normalizedSegments || 'unknown'}`;
};

export const mapPathnameToTrackingScreen = (pathname: string): TrackingScreen => {
  const normalizedPathname = normalizeTrackingPathname(pathname);

  if (normalizedPathname === '/') return 'root';
  if (normalizedPathname === '/auth') return 'auth';
  if (normalizedPathname.startsWith('/auth/')) return 'auth';
  if (normalizedPathname === '/tabs') return 'tabs_root';
  if (normalizedPathname === '/tabs/home') return 'home';
  if (normalizedPathname === '/tabs/user') return 'user';
  if (normalizedPathname === '/tabs/pet') return 'pet';
  if (normalizedPathname === '/tabs/pet/siniestros') return 'siniestros_list';
  if (normalizedPathname === '/tabs/pet/siniestro') return 'siniestro_detail';
  if (normalizedPathname === '/not-found') return 'not_found';

  return buildFallbackTrackingScreen(normalizedPathname);
};

export const incrementCount = (counts: TrackingCountMap, key: string): TrackingCountMap => ({
  ...counts,
  [key]: (counts[key] ?? 0) + 1,
});

export const mergeCountMaps = (
  base: TrackingCountMap = {},
  incoming: TrackingCountMap = {},
): TrackingCountMap => {
  const merged: TrackingCountMap = { ...base };

  Object.entries(incoming).forEach(([key, value]) => {
    merged[key] = (merged[key] ?? 0) + value;
  });

  return merged;
};

export const resolveMostUsedKey = (counts: TrackingCountMap): string | null => {
  let topKey: string | null = null;
  let topValue = -1;

  Object.entries(counts).forEach(([key, value]) => {
    if (value > topValue) {
      topKey = key;
      topValue = value;
    }
  });

  return topKey;
};

export const getTrackingAggregateStorageKey = (userKey: string): string =>
  `${TRACKING_AGGREGATE_PREFIX}::${userKey}`;

export const getTrackingLastSessionStorageKey = (userKey: string): string =>
  `${TRACKING_LAST_SESSION_PREFIX}::${userKey}`;

export const getTrackingActiveSessionStorageKey = (userKey: string): string =>
  `${TRACKING_ACTIVE_SESSION_PREFIX}::${userKey}`;

export const getTrackingTechnicalSnapshotStorageKey = (userKey: string): string =>
  `${TRACKING_TECHNICAL_SNAPSHOT_PREFIX}::${userKey}`;

export const getTrackingTechnicalPendingQueueStorageKey = (userKey: string): string =>
  `${TRACKING_TECHNICAL_PENDING_QUEUE_PREFIX}::${userKey}`;

export const getTrackingPendingQueueStorageKey = (userKey: string): string =>
  `${TRACKING_PENDING_QUEUE_PREFIX}::${userKey}`;

export const getTrackingSyncOverride = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  return parseBooleanOverride(window.localStorage.getItem(TRACKING_SYNC_OVERRIDE_KEY));
};

export const setTrackingSyncOverride = (enabled: boolean | null): void => {
  if (typeof window === 'undefined') return;

  if (enabled === null) {
    window.localStorage.removeItem(TRACKING_SYNC_OVERRIDE_KEY);
    return;
  }

  window.localStorage.setItem(TRACKING_SYNC_OVERRIDE_KEY, enabled ? 'true' : 'false');
};

export const getTrackingDiagnosticsSyncOverride = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  return parseBooleanOverride(
    window.localStorage.getItem(TRACKING_DIAGNOSTICS_SYNC_OVERRIDE_KEY),
  );
};

export const setTrackingDiagnosticsSyncOverride = (enabled: boolean | null): void => {
  if (typeof window === 'undefined') return;

  if (enabled === null) {
    window.localStorage.removeItem(TRACKING_DIAGNOSTICS_SYNC_OVERRIDE_KEY);
    return;
  }

  window.localStorage.setItem(
    TRACKING_DIAGNOSTICS_SYNC_OVERRIDE_KEY,
    enabled ? 'true' : 'false',
  );
};

export const isTrackingSyncEnabled = (): boolean =>
  getTrackingSyncOverride()
    ?? (process.env.REACT_APP_ENABLE_TRACKING_SYNC !== TRACKING_SYNC_DISABLED_ENV_FLAG);

export const isTrackingDiagnosticsSyncEnabled = (): boolean =>
  getTrackingDiagnosticsSyncOverride()
    ?? (process.env.REACT_APP_ENABLE_TRACKING_DIAGNOSTICS_SYNC
      !== TRACKING_DIAGNOSTICS_SYNC_DISABLED_ENV_FLAG);

const parseBooleanOverride = (value: string | null): boolean | null => {
  if (value === null) return null;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

export const getTrackingDebugOverride = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  return parseBooleanOverride(window.localStorage.getItem(TRACKING_DEBUG_STORAGE_KEY));
};

export const setTrackingDebugEnabled = (enabled: boolean | null): void => {
  if (typeof window === 'undefined') return;

  if (enabled === null) {
    window.localStorage.removeItem(TRACKING_DEBUG_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(TRACKING_DEBUG_STORAGE_KEY, enabled ? 'true' : 'false');
};

export const isTrackingDebugEnabled = (): boolean => {
  const override = getTrackingDebugOverride();
  if (override !== null) return override;

  return process.env.REACT_APP_TRACKING_DEBUG === 'true';
};

export const isTrackingVerboseLogsEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_TRACKING_VERBOSE_LOGS === 'true';
  }

  const storedOverride = parseBooleanOverride(
    window.localStorage.getItem(TRACKING_VERBOSE_LOGS_KEY),
  );
  if (storedOverride !== null) return storedOverride;

  return process.env.REACT_APP_TRACKING_VERBOSE_LOGS === 'true';
};

const TRACKING_NOISY_LOG_FRAGMENTS = [
  'No se puede sincronizar snapshot tecnico',
  'Sync de snapshot tecnico',
  'Intentando sincronizar snapshots tecnicos pendientes',
  'Respuesta de snapshot tecnico del backend',
  'Se vació la cola de snapshots tecnicos pendientes',
  'Backend aun no soporta snapshot tecnico',
  'Quedaron snapshots tecnicos pendientes de sync',
  'No se puede sincronizar tracking',
  'Sync de tracking desactivado por configuración',
  'No hay resúmenes pendientes para sincronizar',
  'Intentando sincronizar resúmenes pendientes',
  'Quedaron resúmenes pendientes de sync',
  'Se vació la cola de tracking pendiente',
  'Se ignoró un evento duplicado de permisos',
  'Permiso resuelto',
  'No hay handler de tracking registrado para logout',
];

const shouldSuppressTrackingConsoleLog = (message: string): boolean => {
  if (isTrackingVerboseLogsEnabled()) return false;
  return TRACKING_NOISY_LOG_FRAGMENTS.some((fragment) => message.includes(fragment));
};

export const debugTrackingLog = (message: string, payload?: unknown): void => {
  if (!isTrackingDebugEnabled()) return;
  if (shouldSuppressTrackingConsoleLog(message)) return;

  if (payload === undefined) {
    console.log(`[Tracking] ${message}`);
    return;
  }

  console.log(`[Tracking] ${message}`, payload);
};

export const registerTrackingLogoutFlushHandler = (
  handler: TrackingLogoutFlushHandler,
): (() => void) => {
  trackingLogoutFlushHandler = handler;

  return () => {
    if (trackingLogoutFlushHandler === handler) {
      trackingLogoutFlushHandler = null;
    }
  };
};

export const flushTrackingBeforeLogout = async (): Promise<boolean> => {
  if (!trackingLogoutFlushHandler) {
    debugTrackingLog('No hay handler de tracking registrado para logout');
    return false;
  }

  try {
    const didFlush = await Promise.race<boolean>([
      trackingLogoutFlushHandler().then(() => true),
      new Promise<boolean>((resolve) => {
        window.setTimeout(() => resolve(false), TRACKING_LOGOUT_FLUSH_TIMEOUT_MS);
      }),
    ]);

    if (!didFlush) {
      debugTrackingLog('Timeout al sincronizar tracking antes del logout');
    }

    return didFlush;
  } catch (error) {
    console.error('[Tracking] Error sincronizando antes del logout', error);
    return false;
  }
};

export const parseStoredTrackingJson = <T>(value: string | null): T | null => {
  if (!value) return null;

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.error('[Tracking] Error parsing stored JSON', error);
    return null;
  }
};

export const mergeSessionIntoAggregate = (
  existing: TrackingAggregate | null,
  session: TrackingSessionSummary,
): TrackingAggregate => {
  const screenCounts = mergeCountMaps(existing?.screenCounts ?? {}, session.screenCounts);
  const transitionCounts = mergeCountMaps(
    existing?.transitionCounts ?? {},
    session.transitionCounts,
  );

  return {
    userKey: session.userKey,
    platform: session.platform,
    currentAppVersion: session.appVersion,
    lastLoginAt: session.lastLoginAt,
    totalUsageMs: (existing?.totalUsageMs ?? 0) + session.sessionUsageMs,
    permissionStatus: session.permissionStatus,
    screenCounts,
    transitionCounts,
    mostUsedScreen: resolveMostUsedKey(screenCounts),
    mostUsedFlow: resolveMostUsedKey(transitionCounts),
    updatedAt: new Date().toISOString(),
  };
};

export const buildActiveSessionSnapshot = (
  session: Omit<ActiveTrackingSessionSnapshot, 'updatedAt'>,
): ActiveTrackingSessionSnapshot => ({
  ...session,
  updatedAt: new Date().toISOString(),
});

export const appendPendingTrackingSummary = (
  queue: TrackingPendingQueue,
  summary: TrackingSessionSummary,
): TrackingPendingQueue => {
  const alreadyQueued = queue.some((item) => item.sessionId === summary.sessionId);
  if (alreadyQueued) return queue;

  return [...queue, summary];
};

export const appendPendingTechnicalSnapshot = (
  queue: TrackingTechnicalPendingQueue,
  snapshot: TrackingTechnicalSnapshot,
): TrackingTechnicalPendingQueue => {
  const alreadyQueued = queue.some(
    (item) => item.sessionId === snapshot.sessionId && item.revision === snapshot.revision,
  );

  if (alreadyQueued) return queue;

  const nextQueue = snapshot.phase === 'progress'
    ? queue.filter((item) => !(item.sessionId === snapshot.sessionId && item.phase === 'progress'))
    : queue;
  const appended = [...nextQueue, snapshot];

  if (appended.length <= TRACKING_TECHNICAL_PENDING_QUEUE_MAX_ITEMS) {
    return appended;
  }

  return appended.slice(appended.length - TRACKING_TECHNICAL_PENDING_QUEUE_MAX_ITEMS);
};
